import { api } from 'domains/api';
import { PCPSearchFormType } from '../pcpSchemas';
import { BASE_ANONYMOUS_API_ROUTES } from 'domains/api/base/routes';
import { capitalizeAll } from 'utils/strings/capitalize';

export type NpiPhysicianResultRaw = {
  /** NPI ID */
  number: string;
  enumeration_type: string;
  basic: {
    first_name?: string;
    last_name?: string;
    middle_name?: string;
    credential?: string;
    sole_proprietor?: string;
    gender?: string;
    enumeration_date?: string;
    last_updated?: string;
    status?: string;
  };
  addresses?: {
    address_purpose?: 'MAILING' | 'LOCATION';
    address_type?: string;
    address_1?: string;
    address_2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country_code?: string;
    telephone_number?: string;
    fax_number?: string;
  }[];

  taxonomies?: {
    code?: string;
    taxonomy_group?: string;
    desc?: string;
    state?: string;
    license?: string;
    primary?: boolean;
  }[];
};

export type NpiPhysician = {
  npiNumber: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  faxNumber?: string;
  taxonomyCode?: string;
  office?: string;
  phone?: string;
  credential?: string;
  state?: string;
  city?: string;
  /** Brief description of the physician combining name, office, city, state and NPI number */
  description: string;
};

type NpiResponse = {
  results?: NpiPhysicianResultRaw[];
};

//  @TODO
export async function searchNpiRegistry({
  firstName,
  lastName,
  stateCode,
  city,
}: PCPSearchFormType): Promise<NpiPhysician[]> {
  const params = {
    firstName,
    lastName,
    state: stateCode,
    ...(city ? { city } : {}),
    limit: '100',
  };

  const response = await api.baseAnonymous.get<NpiResponse>(BASE_ANONYMOUS_API_ROUTES.signup['npi-lookup'], {
    params,
  });

  if (!response) {
    throw new Error(`Failed to fetch NPI data: ${response}`);
  }

  const data: NpiResponse = await response;
  return data.results?.map(transformNPIPhysicianResult) || [];
}

const transformNPIPhysicianResult = (data: NpiPhysicianResultRaw): NpiPhysician => {
  const practiceAddress = data.addresses?.find(a => a.address_purpose === 'LOCATION');

  const result = {
    npiNumber: data.number,
    fullName: `${data.basic.first_name} ${data.basic.last_name}`,
    firstName: data.basic.first_name,
    lastName: data.basic.last_name,
    office: practiceAddress?.address_1,
    phone: practiceAddress?.telephone_number,
    credential: data.basic.credential,
    state: practiceAddress?.state,
    city: practiceAddress?.city,
    faxNumber: practiceAddress?.fax_number,
    taxonomyCode: data.taxonomies?.[0]?.code,
    description: '',
  } satisfies NpiPhysician;

  let description = '';

  if (data.basic.credential) {
    description += `${data.basic.credential} | `;
  }

  if (practiceAddress) {
    description += `${result.office}, ${result.city}, ${result.state} `;
  }

  description += `${result.npiNumber}`;

  return {
    ...result,
    description: capitalizeAll(description),
  };
};
